import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib';
import { selectSessionAdvisor } from '@/model/selectors';
import { resetClientEmailState, selectClientEmailState } from '@client';
import { getClientIntakeFormEmailTemplate } from '@shared/email/templates/clientIntakeFormEmailTemplate';
import { Mail } from 'lucide-react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FieldLabel } from './Modal';
import { PersonalizedTextareaWithReset } from './PersonalizedTextareaWithReset';
import { useToast } from './hooks/use-toast';

interface PreviewAndTestEmailProps {
  showPreview: boolean;
  setShowPreview: (show: boolean) => void;
  handleSendTestEmail: () => void;
  isLoading: boolean;
  clientFirstName: string;
  clientLastName?: string;
  advisorFirstName: string;
  organizationDisplayName: string;
  advisorProvidedBody?: string;
  onChangeAdvisorProvidedBody?: (value: string) => void;
  onChangeClientFirstName?: (value: string) => void;
  onChangeClientLastName?: (value: string) => void;
  showClientNameFields?: boolean;
}

export function PreviewAndTestEmail({
  showPreview,
  setShowPreview,
  handleSendTestEmail,
  isLoading,
  clientFirstName,
  clientLastName = '',
  advisorFirstName,
  organizationDisplayName,
  advisorProvidedBody,
  onChangeAdvisorProvidedBody,
  onChangeClientFirstName,
  onChangeClientLastName,
  showClientNameFields = false,
}: PreviewAndTestEmailProps) {
  const dispatch = useDispatch();
  const { status } = useSelector(selectClientEmailState);
  const advisor = useSelector(selectSessionAdvisor);
  const { toast } = useToast();

  useEffect(() => {
    if (status.testEmail === 'complete') {
      toast({
        title: 'Test email sent',
        description: 'Test email sent to ' + advisor.advisorEmail,
        variant: 'default',
        className: '-translate-y-5  border-green-500 bg-green-50 text-green-800',
      });
      dispatch(resetClientEmailState());
    }
  }, [status.testEmail]);

  return (
    <Accordion
      type="single"
      collapsible
      className="mt-2 rounded-md border border-gray-200"
      value={showPreview ? 'preview' : ''}
      onValueChange={value => {
        setShowPreview(value === 'preview');
      }}
    >
      <AccordionItem
        value="preview"
        className="text-sm"
      >
        <AccordionTrigger
          data-testid="customize-email-trigger"
          className={cn(
            'inline-flex items-center text-sm font-semibold text-gray-700',
            'data-[state=open]:border-primary-200 data-[state=open]:border-b',
          )}
          withBackground={false}
        >
          <FieldLabel
            text="Customize & Preview Email"
            className="my-0"
          />
        </AccordionTrigger>
        <AccordionContent className="mb-2 mt-2 px-3 py-3">
          <div className="flex flex-col gap-4">
            {/* Client name fields (conditionally shown) */}
            {showClientNameFields && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-0.5">
                  <FieldLabel text="Client name" />
                  <div className="flex w-full gap-2">
                    <input
                      type="text"
                      data-testid="client-first-name"
                      placeholder="First name"
                      className="border-primary-300 focus:ring-primary block w-full rounded-md border bg-white px-2 py-1.5 text-sm leading-6 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-1"
                      value={clientFirstName}
                      onChange={e => onChangeClientFirstName?.(e.target.value)}
                      disabled={isLoading}
                    />
                    <input
                      type="text"
                      data-testid="client-last-name"
                      placeholder="Last name"
                      className="border-primary-300 focus:ring-primary block w-full rounded-md border bg-white px-2 py-1.5 text-sm leading-6 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-1"
                      value={clientLastName}
                      onChange={e => onChangeClientLastName?.(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Personalized message input */}
            <div className="flex flex-col gap-2">
              <FieldLabel text="Personalized message" />
              <PersonalizedTextareaWithReset
                value={advisorProvidedBody}
                testId="personalized-message"
                onChange={value => onChangeAdvisorProvidedBody?.(value)}
                placeholder="Add a personal message to your invitation email..."
                disabled={isLoading}
              />
            </div>

            {/* Preview box */}
            <div className="space-y-4 rounded-lg border bg-[#f6f6f6] p-4">
              <div className="text-base font-medium text-gray-900">
                Preview of invitation email:
              </div>

              <div
                dangerouslySetInnerHTML={{
                  __html: getClientIntakeFormEmailTemplate({
                    advisorFirstName,
                    intakeFormLink: '',
                    clientFirstName: clientFirstName,
                    advisorProvidedBody: advisorProvidedBody ?? '',
                    isPreview: true,
                  }),
                }}
                className="max-h-[500px] overflow-auto text-gray-900"
              />
            </div>

            {/* Send Test Email button */}
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSendTestEmail}
                disabled={isLoading}
                className="bg-primary-50 text-primary-dark hover:bg-primary-100 font-normal"
              >
                <Mail className="mr-1 h-4 w-4" />
                Send Test Email
              </Button>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
